import { InfoTip } from "@/components/InfoTip";
import { formatDateTime } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { SystemHeader as Header } from "@/types/api";

interface Props {
  header: Header;
  fetchedAt: string;
  stale: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  refreshError?: boolean;
}

export function SystemHeaderCard({
  header,
  fetchedAt,
  stale,
  canRefresh,
  onRefresh,
  refreshing,
  refreshError = false,
}: Props): JSX.Element {
  return (
    <section className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{header.pws_name}</h1>
          <p className="text-sm text-ink-300 mt-1">
            <span className="font-mono">PWS #{header.pws_id}</span>
            <InfoTip {...TIPS.pwsId} />
            {header.status && (
              <>
                <span className="mx-2 text-ink-500">·</span>
                <span className={header.status.toLowerCase().includes("active") ? "pill-ok" : "pill-medium"}>
                  {header.status}
                </span>
                <InfoTip {...TIPS.systemStatus} />
              </>
            )}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {header.pws_type && (
            <>
              <dt className="text-ink-300 inline-flex items-center">
                Type
                <InfoTip
                  label="System type"
                  body="How FDEP classifies this system: Community (serves residents year-round), Non-Transient Non-Community (same non-residents daily, e.g. a school), or Transient Non-Community (changing visitors, e.g. a campground). The type drives which monitoring rules apply."
                />
              </dt>
              <dd>{header.pws_type}</dd>
            </>
          )}
          {header.design_capacity_gpd !== null && (
            <>
              <dt className="text-ink-300 inline-flex items-center">Design capacity<InfoTip {...TIPS.designCapacity} /></dt>
              <dd className="font-mono">{header.design_capacity_gpd.toLocaleString()} gpd</dd>
            </>
          )}
          {header.population !== null && (
            <>
              <dt className="text-ink-300">Population served</dt>
              <dd>{header.population.toLocaleString()}</dd>
            </>
          )}
          {header.city && (
            <>
              <dt className="text-ink-300">City</dt>
              <dd>{header.city}</dd>
            </>
          )}
          {header.operator && (
            <>
              <dt className="text-ink-300 inline-flex items-center">Operator<InfoTip {...TIPS.operator} /></dt>
              <dd>{header.operator}</dd>
            </>
          )}
          {header.phone && (
            <>
              <dt className="text-ink-300">Phone</dt>
              <dd className="font-mono">{header.phone}</dd>
            </>
          )}
        </dl>
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-ink-700 pt-3">
        <p className="text-xs text-ink-400">
          Last fetched: {formatDateTime(fetchedAt)}
          {stale && (
            <span className="ml-2 inline-flex items-center">
              <span className="pill-medium">Stale cache</span>
              <InfoTip {...TIPS.staleData} />
            </span>
          )}
          {!stale && Date.now() - new Date(fetchedAt).getTime() > 30 * 86_400_000 && (
            <span
              className="ml-2 pill-medium"
              title="FDEP data hasn't been refreshed in over 30 days — use Refresh to fetch the latest"
            >
              &gt;30 days old
            </span>
          )}
        </p>
        {canRefresh && (
          <span className="inline-flex items-center gap-2">
            {refreshError && (
              <span className="text-xs text-severity-high" role="alert">
                Refresh failed — FDEP may be unavailable; showing last known data.
              </span>
            )}
            <button onClick={onRefresh} disabled={refreshing} className="btn-primary text-xs">
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </span>
        )}
      </div>
    </section>
  );
}
